import { useState } from 'react';
import { Loader, Server, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SmtpConnectRequest, SmtpEncryption } from '@clareefai/types';

interface Props {
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (request: SmtpConnectRequest) => void;
  onCancel?: () => void;
  initial?: Partial<SmtpConnectRequest>;
}

const PORT_DEFAULTS: Record<SmtpEncryption, number> = {
  SSL: 465,
  STARTTLS: 587,
  NONE: 25,
};

export default function SmtpConfigForm({ isSubmitting, errorMessage, onSubmit, onCancel, initial }: Props) {
  const { t } = useTranslation();
  const [host, setHost] = useState(initial?.host ?? '');
  const [encryption, setEncryption] = useState<SmtpEncryption>(initial?.encryption ?? 'STARTTLS');
  const [port, setPort] = useState<number>(initial?.port ?? PORT_DEFAULTS[initial?.encryption ?? 'STARTTLS']);
  const [portTouched, setPortTouched] = useState(initial?.port != null);
  const [username, setUsername] = useState(initial?.username ?? '');
  const [password, setPassword] = useState('');
  const [fromEmail, setFromEmail] = useState(initial?.fromEmail ?? '');
  const [fromName, setFromName] = useState(initial?.fromName ?? '');

  const handleEncryptionChange = (next: SmtpEncryption) => {
    setEncryption(next);
    if (!portTouched) {
      setPort(PORT_DEFAULTS[next]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      host: host.trim(),
      port,
      encryption,
      username: username.trim(),
      password,
      fromEmail: fromEmail.trim(),
      fromName: fromName.trim() || undefined,
    });
  };

  const showFromMismatchHint =
    fromEmail.trim().length > 0 &&
    username.trim().length > 0 &&
    fromEmail.trim().toLowerCase() !== username.trim().toLowerCase();

  const isValid =
    host.trim().length > 0 &&
    port > 0 &&
    port < 65536 &&
    username.trim().length > 0 &&
    password.length > 0 &&
    fromEmail.trim().length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 mb-2 text-gray-700">
        <Server className="w-5 h-5 text-purple-600" />
        <h3 className="font-bold">{t('settings.emailSender.smtpFormHeading')}</h3>
      </div>
      <p className="text-sm text-gray-600">{t('settings.emailSender.smtpFormIntro')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.emailSender.smtpHost')}
          </label>
          <input
            type="text"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder={t('settings.emailSender.smtpHostPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.emailSender.smtpPort')}
          </label>
          <input
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => {
              setPort(Number(e.target.value));
              setPortTouched(true);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('settings.emailSender.smtpEncryption')}
        </label>
        <div className="grid grid-cols-3 gap-2">
          {(['SSL', 'STARTTLS', 'NONE'] as SmtpEncryption[]).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleEncryptionChange(opt)}
              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                encryption === opt
                  ? 'bg-purple-50 border-purple-400 text-purple-700 font-medium'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              {t(`settings.emailSender.smtpEncryption${opt === 'SSL' ? 'Ssl' : opt === 'STARTTLS' ? 'Starttls' : 'None'}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.emailSender.smtpUsername')}
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.emailSender.smtpPassword')}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
            autoComplete="new-password"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.emailSender.smtpFromEmail')}
          </label>
          <input
            type="email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            required
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.emailSender.smtpFromName')}
          </label>
          <input
            type="text"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            autoComplete="off"
          />
        </div>
      </div>

      {showFromMismatchHint && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          {t('settings.emailSender.smtpFromMismatchHint')}
        </div>
      )}

      {errorMessage && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
          {isSubmitting ? t('settings.emailSender.smtpVerifying') : t('settings.emailSender.smtpVerifyAndConnect')}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
      </div>
    </form>
  );
}
